import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    IconButton,
    TextField,
    Typography,
    Chip,
    Tooltip,
    CircularProgress,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    FormControlLabel,
    RadioGroup,
    Radio,
    Autocomplete,
    InputAdornment
} from '@mui/material';
import {
    Edit,
    Delete,
    Label as LabelIcon,
    Search as SearchIcon,
    Clear as ClearIcon
} from '@mui/icons-material';
import { DataGrid, GridToolbarContainer, GridToolbar } from '@mui/x-data-grid';
import { deleteContact, updateContactTags, getContactTags } from '../../services/contactService';

// Tag Operations Dialog component - оптимизированный
const TagOperationsDialog = React.memo(({
                                            open, onClose, onApply, selectedCount, tagOptions, loadingTags
                                        }) => {
    const [selectedTags, setSelectedTags] = useState([]);
    const [operation, setOperation] = useState('add');

    // Reset selected tags when dialog opens/closes
    useEffect(() => {
        if (!open) {
            setSelectedTags([]);
            setOperation('add');
        }
    }, [open]);

    const handleTagChange = useCallback((event, newValue) => {
        setSelectedTags(newValue);
    }, []);

    const handleOperationChange = useCallback((e) => {
        setOperation(e.target.value);
    }, []);

    const handleApply = useCallback(() => {
        if (selectedTags.length > 0) {
            const tagsString = selectedTags.join(', ');
            onApply(tagsString, operation);
        }
        onClose();
    }, [selectedTags, operation, onApply, onClose]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Tag Operations</DialogTitle>
            <DialogContent>
                <Box sx={{ p: 1 }}>
                    <Autocomplete
                        multiple
                        loading={loadingTags}
                        options={tagOptions}
                        value={selectedTags}
                        onChange={handleTagChange}
                        freeSolo
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    label={option}
                                    {...getTagProps({ index })}
                                    size="small"
                                    key={index}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Tags"
                                placeholder="Add tags"
                            />
                        )}
                    />

                    <FormControl component="fieldset">
                        <RadioGroup
                            name="tag-operation"
                            value={operation}
                            onChange={handleOperationChange}
                        >
                            <FormControlLabel
                                value="add"
                                control={<Radio />}
                                label="Add tags to selected contacts"
                            />
                            <FormControlLabel
                                value="remove"
                                control={<Radio />}
                                label="Remove tags from selected contacts"
                            />
                            <FormControlLabel
                                value="invert"
                                control={<Radio />}
                                label="Invert tags (add if missing, remove if present)"
                            />
                        </RadioGroup>
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    onClick={handleApply}
                    variant="contained"
                    color="primary"
                    disabled={selectedTags.length === 0}
                >
                    Apply to {selectedCount} contacts
                </Button>
            </DialogActions>
        </Dialog>
    );
});

TagOperationsDialog.displayName = 'TagOperationsDialog';

// Search and Filter Toolbar - новый компонент для поиска по всем данным
const SearchAndFilterToolbar = React.memo(({
                                               onSearch, onTagsFilter, onAddTagToFilter, tagOptions, loadingTags, searchValue = '', selectedTagsValue = []
                                           }) => {
    const [searchTerm, setSearchTerm] = useState(searchValue);
    const [selectedTags, setSelectedTags] = useState(selectedTagsValue);

    // Синхронизируем локальное состояние с пропсами
    useEffect(() => {
        setSearchTerm(searchValue);
    }, [searchValue]);

    useEffect(() => {
        setSelectedTags(selectedTagsValue);
    }, [selectedTagsValue]);

    const handleSearchChange = useCallback((e) => {
        const value = e.target.value;
        setSearchTerm(value);

        // Debounced search - поиск с задержкой
        const timeoutId = setTimeout(() => {
            onSearch(value);
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [onSearch]);

    const handleTagsChange = useCallback((event, newValue) => {
        setSelectedTags(newValue);
        onTagsFilter(newValue);
    }, [onTagsFilter]);

    const handleClearSearch = useCallback(() => {
        setSearchTerm('');
        onSearch('');
    }, [onSearch]);

    const handleClearTags = useCallback(() => {
        setSelectedTags([]);
        onTagsFilter([]);
    }, [onTagsFilter]);

    return (
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Box sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'flex-start', // Выравнивание по верху
                flexWrap: 'wrap' // Перенос на новую строку при необходимости
            }}>
                {/* Поле поиска */}
                <TextField
                    size="small"
                    placeholder="Search all contacts..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    sx={{
                        minWidth: 280,
                        maxWidth: 400,
                        flexGrow: 1 // Занимает доступное пространство
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                        endAdornment: searchTerm && (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={handleClearSearch}>
                                    <ClearIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                {/* Поле фильтрации по тегам */}
                <Autocomplete
                    multiple
                    loading={loadingTags}
                    options={tagOptions || []}
                    value={selectedTags}
                    onChange={handleTagsChange}
                    freeSolo
                    sx={{
                        minWidth: 250,
                        maxWidth: 700,
                        flexGrow: 1 // Занимает доступное пространство
                    }}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                label={option}
                                {...getTagProps({ index })}
                                size="small"
                                key={index}
                                color="primary"
                                variant="outlined"
                            />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Filter by Tags"
                            placeholder="Select or type tags..."
                            size="small"
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <>
                                        <InputAdornment position="start">
                                            <LabelIcon fontSize="small" />
                                        </InputAdornment>
                                        {params.InputProps.startAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />

                {/* Индикатор загрузки тегов */}
                {loadingTags && (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        minHeight: 40, // Высота соответствует полям
                        px: 2
                    }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" sx={{ ml: 1 }} color="text.secondary">
                            Загрузка...
                        </Typography>
                    </Box>
                )}

                {/* Кнопка очистки тегов */}
                {selectedTags.length > 0 && (
                    <Button
                        size="small"
                        onClick={handleClearTags}
                        variant="outlined"
                        color="secondary"
                        sx={{
                            minHeight: 40, // Высота соответствует полям
                            whiteSpace: 'nowrap' // Текст не переносится
                        }}
                    >
                        Clear Tags ({selectedTags.length})
                    </Button>
                )}
            </Box>
        </Box>
    );
});

SearchAndFilterToolbar.displayName = 'SearchAndFilterToolbar';

// Custom toolbar component - упрощенный
const ContactsToolbar = React.memo(({ selectedContacts, onApplyTags, onDeleteContacts }) => {
    const [tagOperationsDialogOpen, setTagOperationsDialogOpen] = useState(false);

    const handleTagOperationsOpen = useCallback(() => {
        setTagOperationsDialogOpen(true);
    }, []);

    const handleTagOperationsClose = useCallback(() => {
        setTagOperationsDialogOpen(false);
    }, []);

    const handleDeleteClick = useCallback(() => {
        if (window.confirm(`Are you sure you want to delete ${selectedContacts.length} selected contacts?`)) {
            onDeleteContacts();
        }
    }, [selectedContacts.length, onDeleteContacts]);

    if (selectedContacts.length === 0) {
        return <GridToolbar />;
    }

    return (
        <GridToolbarContainer>
            <Button
                color="primary"
                variant="contained"
                startIcon={<LabelIcon />}
                onClick={handleTagOperationsOpen}
                size="small"
                sx={{ ml: 2 }}
            >
                Tag Operations ({selectedContacts.length} selected)
            </Button>

            <Button
                color="error"
                variant="contained"
                startIcon={<Delete />}
                onClick={handleDeleteClick}
                size="small"
                sx={{ ml: 1 }}
            >
                Delete ({selectedContacts.length} selected)
            </Button>

            <TagOperationsDialog
                open={tagOperationsDialogOpen}
                onClose={handleTagOperationsClose}
                onApply={onApplyTags}
                selectedCount={selectedContacts.length}
            />
        </GridToolbarContainer>
    );
});

ContactsToolbar.displayName = 'ContactsToolbar';

const ContactsTable = ({
                           contacts,
                           loading,
                           pagination,
                           onPageChange,
                           onRowsPerPageChange,
                           onSearch,
                           onTagsFilter,
                           onEdit,
                           onRefresh,
                           onSelectionChange,
                           selectedContacts,
                           onDeleteContacts,
                           tagOptions,
                           loadingTags,
                           searchValue = '',
                           selectedTagsValue = []
                       }) => {
    const [deleteLoading, setDeleteLoading] = useState(false);

    const handleApplyTags = useCallback(async (tagsToApply, operation = 'add') => {
        try {
            const results = await updateContactTags(selectedContacts, tagsToApply, operation);

            if (results.failed?.length > 0) {
                console.error('Some contacts could not be updated:', results.failed);
                alert(`Tags ${operation}ed with some errors. ${results.success.length} succeeded, ${results.failed.length} failed.`);
            } else {
                alert(`Tags ${operation}ed successfully for ${results.success.length} contacts`);
            }

            onRefresh();
        } catch (error) {
            console.error('Error applying tags:', error);
            alert(`Failed to ${operation} tags: ${error.message}`);
        }
    }, [selectedContacts, onRefresh]);

    const handleDelete = useCallback(async (contact) => {
        if (window.confirm(`Are you sure you want to delete ${contact.name}?`)) {
            setDeleteLoading(true);
            try {
                await deleteContact(contact.id);
                onRefresh();
            } catch (error) {
                console.error('Error deleting contact:', error);
                alert('Failed to delete contact');
            } finally {
                setDeleteLoading(false);
            }
        }
    }, [onRefresh]);

    // Функция для добавления тега к фильтрам (не заменяя существующие)
    const handleAddTagToFilter = useCallback((tagToAdd) => {
        // Убедимся, что selectedTagsValue - это массив
        const currentTags = Array.isArray(selectedTagsValue) ? selectedTagsValue : [];

        // Проверим, есть ли уже такой тег в фильтрах
        if (!currentTags.includes(tagToAdd)) {
            const newTags = [...currentTags, tagToAdd];
            onTagsFilter(newTags);
        }
    }, [selectedTagsValue, onTagsFilter]);

    // Render tags as chips
    const renderTags = useCallback((tagsString) => {
        if (!tagsString) return null;

        const tagsArray = tagsString.split(',').map(tag => tag.trim()).filter(Boolean);

        return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {tagsArray.map((tag, index) => (
                    <Chip
                        key={index}
                        label={tag}
                        size="small"
                        onClick={() => handleAddTagToFilter(tag)}
                        sx={{
                            cursor: 'pointer',
                            '&:hover': {
                                backgroundColor: 'primary.main',
                                color: 'primary.contrastText',
                            }
                        }}
                        color={
                            Array.isArray(selectedTagsValue) && selectedTagsValue.includes(tag)
                                ? 'primary'
                                : 'default'
                        }
                        variant={
                            Array.isArray(selectedTagsValue) && selectedTagsValue.includes(tag)
                                ? 'filled'
                                : 'outlined'
                        }
                    />
                ))}
            </Box>
        );
    }, [handleAddTagToFilter, selectedTagsValue]);

    // Define columns for DataGrid - мемоизируем
    const columns = useMemo(() => [
        { field: 'name', headerName: 'Name', width: 200, flex: 1 },
        { field: 'phone', headerName: 'Phone', width: 150, flex: 1 },
        { field: 'email', headerName: 'Email', width: 200, flex: 1 },
        {
            field: 'tags',
            headerName: 'Tags',
            width: 200,
            flex: 1,
            renderCell: (params) => renderTags(params.value)
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 150,
            flex: 0.7,
            sortable: false,
            renderCell: (params) => (
                <Box>
                    <Tooltip title="Edit">
                        <IconButton onClick={() => onEdit(params.row)} size="small">
                            <Edit />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton
                            onClick={() => handleDelete(params.row)}
                            size="small"
                            disabled={deleteLoading}
                        >
                            <Delete />
                        </IconButton>
                    </Tooltip>
                </Box>
            )
        }
    ], [renderTags, onEdit, handleDelete, deleteLoading]);

    const handlePaginationChange = useCallback((newModel) => {
        if (newModel.page !== pagination.page - 1) {
            onPageChange(newModel.page + 1);
        }
        if (newModel.pageSize !== pagination.limit) {
            onRowsPerPageChange(newModel.pageSize);
        }
    }, [pagination.page, pagination.limit, onPageChange, onRowsPerPageChange]);

    if (loading && !contacts.length) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', height: '63vh' }}>
            <SearchAndFilterToolbar
                onSearch={onSearch}
                onTagsFilter={onTagsFilter}
                onAddTagToFilter={handleAddTagToFilter}
                tagOptions={tagOptions}
                loadingTags={loadingTags}
                searchValue={searchValue}
                selectedTagsValue={selectedTagsValue}
            />

            <DataGrid
                rows={contacts || []}
                columns={columns}
                paginationModel={{
                    page: pagination.page - 1,
                    pageSize: pagination.limit
                }}
                pageSizeOptions={[10, 25, 50, 100]}
                pagination
                paginationMode="server"
                rowCount={pagination.total}
                onPaginationModelChange={handlePaginationChange}
                loading={loading}
                checkboxSelection
                disableRowSelectionOnClick
                onRowSelectionModelChange={onSelectionChange}
                getRowClassName={(params) => {
                    // Помечаем строку красным, если у контакта отсутствует WhatsApp
                    if (params.row.whatsapp_exists === false) {
                        return 'no-whatsapp-row';
                    }
                    return '';
                }}
                sx={{
                    border: 0,
                    '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: 'grey.50'
                    },
                    // Стили для строк без WhatsApp
                    '& .no-whatsapp-row': {
                        backgroundColor: 'rgba(244, 67, 54, 0.1)', // Красный фон с прозрачностью
                        '&:hover': {
                            backgroundColor: 'rgba(244, 67, 54, 0.15)', // Немного темнее при наведении
                        },
                        '&.Mui-selected': {
                            backgroundColor: 'rgba(244, 67, 54, 0.2)', // Темнее при выделении
                            '&:hover': {
                                backgroundColor: 'rgba(244, 67, 54, 0.25)',
                            },
                        }
                    }
                }}
            />
        </Box>
    );
};

export { TagOperationsDialog };
export default React.memo(ContactsTable);